import { ReactNode, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { i18n } from '@/i18n/config';
import { AuthProvider } from '@/auth/AuthContext';
import { AccessibilityPreferencesSync } from '@/components/a11y/AccessibilityPreferencesSync';
import { ReadingMaskOverlay } from '@/components/a11y/ReadingMaskOverlay';
import { PRIMARY_COLOR_TOKENS, useThemeStore } from '@/stores/theme-store';
import { Toaster } from '@/components/ui/Toaster';
import { CommandPalette } from '@/components/command-palette/CommandPalette';
import { initMfaRecaptchaBridge } from '@/extension/mfa-recaptcha-bridge';

const isExtensionRuntime =
  typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
const isElectronRuntime =
  typeof window !== 'undefined' && window.electron !== undefined;

// HashRouter for extension + Electron (non-http origins); BrowserRouter for hosted web.
const AppRouter = isExtensionRuntime || isElectronRuntime ? HashRouter : BrowserRouter;

if (isExtensionRuntime) {
  window.troveApp = { ...window.troveApp, isExtension: () => true };
  initMfaRecaptchaBridge();
}

interface ProvidersProps {
  children: ReactNode;
}

/**
 * Root providers wrapper
 * Combines all context providers needed by the app
 */
export function Providers({ children }: ProvidersProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <AppRouter>
        <LocaleHtmlSync />
        <AuthProvider>
          <ThemeProvider>
            <AccessibilityPreferencesSync />
            <ReadingMaskOverlay />
            {children}
            <CommandPalette />
            <Toaster />
          </ThemeProvider>
        </AuthProvider>
      </AppRouter>
    </I18nextProvider>
  );
}

/** Keep `<html lang>` and `dir` aligned with i18n for a11y and browser behavior. */
function LocaleHtmlSync() {
  useEffect(() => {
    const apply = (lng: string) => {
      const resolved = lng.split('-')[0] || 'en';
      document.documentElement.lang = resolved;
      document.documentElement.dir = i18n.dir(resolved);
    };
    apply(i18n.resolvedLanguage || i18n.language || 'en');
    const handler = (lng: string) => apply(lng);
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);
  return null;
}

/**
 * Theme provider that applies theme class and primary color variables to document.
 *
 * Zustand persist rehydrates asynchronously. The default store has primaryColor "blue", so an
 * effect that runs before hydration would overwrite theme-init.js / localStorage accent with blue.
 * We only apply --primary after persist.hasHydrated(), and re-apply on onFinishHydration.
 */
function ThemeProvider({ children }: { children: ReactNode }) {
  const { theme, primaryColor, getEffectiveTheme } = useThemeStore();

  useLayoutEffect(() => {
    const applyTheme = () => {
      if (!useThemeStore.persist.hasHydrated()) {
        return;
      }
      const root = document.documentElement;
      const body = document.body;
      const state = useThemeStore.getState();
      const effectiveTheme = state.getEffectiveTheme();
      const colorToken = PRIMARY_COLOR_TOKENS[state.primaryColor];

      if (effectiveTheme === 'dark') {
        root.classList.add('dark');
        body.classList.add('dark');
      } else {
        root.classList.remove('dark');
        body.classList.remove('dark');
      }

      root.style.setProperty('--primary', colorToken.primary);
      root.style.setProperty('--primary-foreground', colorToken.primaryForeground);
      body.style.setProperty('--primary', colorToken.primary);
      body.style.setProperty('--primary-foreground', colorToken.primaryForeground);
    };

    const unsubFinish = useThemeStore.persist.onFinishHydration(() => {
      applyTheme();
    });

    if (useThemeStore.persist.hasHydrated()) {
      applyTheme();
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', applyTheme);
    return () => {
      unsubFinish();
      mediaQuery.removeEventListener('change', applyTheme);
    };
  }, [theme, primaryColor, getEffectiveTheme]);

  return <>{children}</>;
}
